import type {
  GeneCell,
  Hit,
  KiegIO,
  LineRecord,
  PedigreeCheck,
  PhenoIO,
  RegisterSearchField,
  Supplements,
  ValuesIO,
  WorkItem,
} from "@api/generated/model";
import {
  getRegisterRecordQueryKey,
  getRegisterSearchQueryKey,
  getRegisterSupplementsQueryKey,
  registerCheck,
  useRegisterAddSid,
  useRegisterCreate,
  useRegisterRecord,
  useRegisterSave,
  useRegisterSaveGenes,
  useRegisterSaveSupplement,
  useRegisterSearch,
  useRegisterSupplements,
} from "@api/generated/register/register";
import { useUserMe } from "@api/generated/user/user";
import { usePreview } from "@app/preview";
import { hasRole, useSettings } from "@app/workspace";
import { keyColumn } from "@features/genealogy/GenotypeParts";
import { keyText } from "@features/genealogy/genotype";
import "@features/genealogy/genealogy.css";
import { hint } from "@i18n";
import { Button, Loader, Modal, NumberInput, SegmentedControl, Select, Tabs, Textarea, TextInput } from "@mantine/core";
import { useDebouncedValue } from "@mantine/hooks";
import { notifications } from "@mantine/notifications";
import { ConfirmModal } from "@shell/Dialogs";
import { keepPreviousData, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { AnimatedTabsList } from "@ui/AnimatedTabsList";
import { DateField } from "@ui/DateField";
import { formatNumber, num } from "@ui/format";
import { PageHeader } from "@ui/PageHeader";
import { Splitter } from "@ui/Splitter";
import { RecentItems } from "@ui/StartCard";
import { type Column, DataTable, useDataTable } from "@ui/table/DataTable";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";

const FIELDS: RegisterSearchField[] = ["all", "fajta", "torzs_nev", "rov", "pedigre", "paz"];
const TEXTS = ["torzs_szam", "torzs_nev", "fajta", "rov", "pgm", "gh", "orsz", "ceg", "mta_gene_bank"] as const;
const NUMBERS = ["reg", "canc"] as const;
const NAMES = new Set<string>(["torzs_nev", "fajta", "rov"]); // what the search and the display name read
const NEW_FIELDS = ["fajta", "torzs_nev", "rov", "pgm", "gh"] as const;
const NO_CHECK: PedigreeCheck = { problems: [], mother: null, father: null, existing: [] };
const TABS = ["line", "pheno", "kieg", "genes"] as const;
type Tab = (typeof TABS)[number];

type Kind = "text" | "number" | "date" | "memo";
// the phenotype (Pheno) and passport (Kieg) fields as the editor shows them, in the legacy column order
const PHENO: [keyof PhenoIO, Kind][] = [
  ["kt", "text"],
  ["pheno_gh", "text"],
  ["pheno_szemszin", "text"],
  ["pheno_lisztszin", "text"],
  ["pheno_levelszin", "text"],
  ["pheno_kemenyseg", "text"],
  ["pheno_kalaszszin", "text"],
  ["waxy", "number"],
  ["amiloz", "number"],
];
const KIEG: [keyof KiegIO, Kind][] = [
  ["genus", "text"],
  ["species", "text"],
  ["ssp", "text"],
  ["var", "text"],
  ["forma", "text"],
  ["cultivar_ped", "text"],
  ["teljes_nev", "text"],
  ["faj_rov", "text"],
  ["szinonima", "text"],
  ["ploida", "text"],
  ["genom", "text"],
  ["donor_name", "text"],
  ["donor_id", "text"],
  ["other_id", "text"],
  ["arrival_date", "date"],
  ["origin", "text"],
  ["ngbab", "text"],
  ["collection_site", "text"],
  ["letezo", "text"],
  ["mvgb", "text"],
  ["whealbi_nr", "text"],
  ["megj", "memo"],
];

export const Route = createFileRoute("/genealogy/register")({
  validateSearch: z.object({
    q: z.string().optional(),
    field: z.enum(FIELDS as [RegisterSearchField, ...RegisterSearchField[]]).optional(),
    paz: z.number().int().optional(),
    sid: z.number().int().optional(),
    tab: z.enum(TABS).optional().catch(undefined),
  }),
  component: RegisterPage,
});

/** Who may edit what (the legacy's two levels): the register record, a new combination and a new SID were the
 *  password-guarded Pedigree management (administrators); the phenotype and passport rows were the open Show Trial
 *  Data grid (breeders), the Genes matrix is the breeders' and the lab's. */
type Rights = { register: boolean; supplements: boolean; genes: boolean };

/** Genealógia → Törzsregiszter (the legacy Pedigree management): the combinations (PAz) and lines (SID) found by name,
 *  pedigree or key; a line's register fields and its combination's pedigree edited, a new combination, a new SID; the
 *  line's phenotype, passport and Genes matrix rows. */
function RegisterPage() {
  const { t } = useTranslation();
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const roles = useUserMe().data?.roles;
  const rights: Rights = {
    register: hasRole(roles, "admin"),
    supplements: hasRole(roles, "nemesito"),
    genes: hasRole(roles, "nemesito", "labor"),
  };
  const [q, setQ] = useState(search.q ?? "");
  const [asked] = useDebouncedValue(q.trim(), 300);
  const field = search.field ?? "all";
  const found = useRegisterSearch({ q: asked, field }, { query: { enabled: asked.length > 0, placeholderData: keepPreviousData } });
  const [creating, setCreating] = useState(false);
  const pick = (paz: number, sid: number) => void navigate({ search: (s) => ({ ...s, q: asked || undefined, paz, sid }) });
  const right = rights.register ? "admin" : rights.supplements ? "breeder" : rights.genes ? "lab" : "reader";
  const columns = useMemo<Column<Hit>[]>(
    () => [
      keyColumn((h) => h.key),
      {
        id: "name",
        header: t("register.name"),
        value: (h) => h.name,
        cellClassName: (h) => (h.key.sid === 0 ? "mono" : undefined),
        cell: (h) => (h.key.paz === search.paz && h.key.sid === search.sid ? <b>{h.name ?? "—"}</b> : (h.name ?? "—")),
      },
      { id: "pgm", header: "PGM", value: (h) => h.pgm },
    ],
    [t, search.paz, search.sid],
  );
  // the server sends at most its limit of hits (``truncated``): the sort and the filters apply to those
  const table = useDataTable("genealogy.register.lines", columns, found.data?.hits, { partial: !!found.data?.truncated });
  return (
    <div className="page fill">
      <PageHeader
        title={t("register.title")}
        subtitle={t("register.subtitle")}
        actions={
          <div className="hstack" style={{ gap: 8 }}>
            <span className="sm mu">{t(`register.rights.${right}`)}</span>
            {rights.register && (
              <Button size="compact-sm" onClick={() => setCreating(true)}>
                {t("register.newCombination")}
              </Button>
            )}
          </div>
        }
      />
      <div className="fill-row">
        <section className="card stack tr-list">
          <div className="inset stack" style={{ gap: 8, paddingBottom: 0 }}>
            <TextInput
              label={t("register.search")}
              placeholder={t("register.searchHelp")}
              description={!asked ? t("register.searchEmpty") : undefined}
              value={q}
              onChange={(e) => setQ(e.currentTarget.value)}
            />
            <SegmentedControl
              size="xs"
              value={field}
              onChange={(v) => void navigate({ search: (s) => ({ ...s, field: v as RegisterSearchField }) })}
              data={FIELDS.map((f) => ({ value: f, label: t(`register.searchField.${f}`) }))}
            />
          </div>
          <DataTable
            table={table}
            rowKey={(h) => keyText(h.key)}
            className="grow"
            rowProps={(h) => ({
              className: `click${h.key.paz === search.paz && h.key.sid === search.sid ? " sel" : ""}`,
              onClick: () => pick(h.key.paz, h.key.sid),
            })}
          />
          <div className="sm mu inset">
            {found.isFetching ? (
              <Loader size="xs" />
            ) : found.data ? (
              t("register.results", { count: formatNumber(found.data.hits.length), combination: formatNumber(found.data.combinations) }) +
              (found.data.truncated ? ` · ${t("register.truncated")}` : "")
            ) : null}
          </div>
        </section>
        <Splitter cssVar="--tr-list-w" side="start" min={280} max={800} />
        <div className="grow stack" style={{ gap: 16, minWidth: 0, overflowY: "auto" }}>
          {search.paz != null && search.sid != null ? (
            <Editor
              key={`${search.paz}/${search.sid}`}
              paz={search.paz}
              sid={search.sid}
              rights={rights}
              tab={search.tab ?? "line"}
              onTab={(tab) => void navigate({ search: (s) => ({ ...s, tab: tab === "line" ? undefined : tab }) })}
              onOpen={pick}
            />
          ) : (
            <section className="card pad stack" style={{ gap: 8 }}>
              <span className="sm mu">{t("register.selectPrompt")}</span>
              <RecentItems kind="genotype" max={6} hrefOf={registerHref} />
            </section>
          )}
        </div>
      </div>
      <NewCombination opened={creating} onClose={() => setCreating(false)} onCreated={(paz, sid) => pick(paz, sid)} />
    </div>
  );
}

function Editor(props: {
  paz: number;
  sid: number;
  rights: Rights;
  tab: Tab;
  onTab: (tab: Tab) => void;
  onOpen: (paz: number, sid: number) => void;
}) {
  const { t } = useTranslation();
  const record = useRegisterRecord(props.paz, props.sid).data;
  if (!record) return <Loader size="sm" />;
  return (
    <Tabs value={props.tab} onChange={(v) => v && props.onTab(v as Tab)} keepMounted={false}>
      <AnimatedTabsList mb="md">
        {TABS.map((x) => (
          <Tabs.Tab key={x} value={x}>
            {t(`register.tab.${x}`)}
          </Tabs.Tab>
        ))}
      </AnimatedTabsList>
      <Tabs.Panel value="line">
        <div className="stack" style={{ gap: 16 }}>
          <RecordForm record={record} canEdit={props.rights.register} onOpen={props.onOpen} />
        </div>
      </Tabs.Panel>
      {TABS.filter((x) => x !== "line").map((x) => (
        <Tabs.Panel key={x} value={x}>
          <SupplementTab paz={props.paz} sid={props.sid} tab={x} rights={props.rights} />
        </Tabs.Panel>
      ))}
    </Tabs>
  );
}

function initial(record: LineRecord): ValuesIO {
  const r = record.register;
  return Object.fromEntries([...TEXTS, ...NUMBERS, "torzs_megj" as const].map((f) => [f, r[f]]));
}

/** The pedigree as typed, checked by the server (Purdy notation) with its parents looked up in the register and the
 *  combinations that have it already; the stored pedigree's check comes with the record, an empty pedigree needs none. */
function usePedigreeCheck(pedigree: string, stored?: { text: string; check: PedigreeCheck }) {
  const text = pedigree.trim();
  const own = stored && text === stored.text.trim();
  const asked = usePreview("pedigree-check", text && !own ? { pedigree: text } : null, (b, signal) => registerCheck(b, { signal }));
  return !text ? NO_CHECK : own ? stored.check : asked.data;
}

/** After a register write: the record again, and the search results when a name or the pedigree changed. */
function useInvalidateRegister() {
  const client = useQueryClient();
  const search = getRegisterSearchQueryKey()[0];
  return (key: { paz: number; sid: number } | null, names: boolean) => {
    if (key) void client.invalidateQueries({ queryKey: getRegisterRecordQueryKey(key.paz, key.sid) });
    if (names) void client.invalidateQueries({ queryKey: [search] });
  };
}

function RecordForm({ record, canEdit, onOpen }: { record: LineRecord; canEdit: boolean; onOpen: (paz: number, sid: number) => void }) {
  const { t } = useTranslation();
  const r = record.register;
  const init = useMemo(() => initial(record), [record]);
  const [values, setValues] = useState<ValuesIO>(init);
  const [pedigree, setPedigree] = useState(r.pedigre ?? "");
  const shown = usePedigreeCheck(pedigree, { text: r.pedigre ?? "", check: record.check });
  const others = (shown?.existing ?? []).filter((l) => l.key.paz !== r.paz);
  const changed: ValuesIO = Object.fromEntries(
    Object.entries(values).filter(([k, v]) => (v ?? null) !== (init[k as keyof ValuesIO] ?? null)),
  );
  const pedigreeChanged = pedigree.trim() !== (r.pedigre ?? "").trim();
  const dirty = Object.keys(changed).length > 0 || pedigreeChanged;
  const invalidate = useInvalidateRegister();
  const save = useRegisterSave({
    mutation: {
      onSuccess: () => {
        notifications.show({ color: "green", message: t("register.saved") });
        invalidate(r, pedigreeChanged || Object.keys(changed).some((k) => NAMES.has(k)));
      },
    },
  });
  const [askingSid, setAskingSid] = useState(false);
  const addSid = useRegisterAddSid({
    mutation: {
      onSuccess: (out) => {
        notifications.show({ color: "green", message: t("register.newSidDone", { key: out.key ? keyText(out.key) : "" }) });
        invalidate(null, true);
        if (out.key) onOpen(out.key.paz, out.key.sid);
      },
    },
  });
  const set = (p: Partial<ValuesIO>) => setValues((v) => ({ ...v, ...p }));
  return (
    <>
      <section className="card pad stack" style={{ gap: 12 }}>
        <div className="hstack" style={{ gap: 10 }}>
          <h2 className="h2">{r.name ?? keyText(r)}</h2>
          <span className="sm mu grow">{t("register.nameRule")}</span>
          <Link to="/genealogy/genotype" search={{ paz: r.paz, sid: r.sid }} className="sm">
            {t("register.genotypeSheet")}
          </Link>
        </div>
        <div className="tr-fields">
          <TextInput size="xs" label="PAz" value={String(r.paz)} readOnly />
          <TextInput size="xs" label="SID" value={String(r.sid)} readOnly />
          {TEXTS.map((f) => (
            <TextInput
              key={f}
              size="xs"
              label={t(`register.field.${f}`)}
              value={values[f] ?? ""}
              readOnly={!canEdit}
              onChange={(e) => set({ [f]: e.currentTarget.value })}
            />
          ))}
          {NUMBERS.map((f) => (
            <NumberInput
              key={f}
              size="xs"
              label={t(`register.field.${f}`)}
              value={values[f] ?? ""}
              readOnly={!canEdit}
              allowDecimal={false}
              onChange={(v) => set({ [f]: num(v) })}
            />
          ))}
        </div>
        <Textarea
          size="xs"
          label={t("register.field.torzs_megj")}
          autosize
          minRows={1}
          value={values.torzs_megj ?? ""}
          readOnly={!canEdit}
          onChange={(e) => set({ torzs_megj: e.currentTarget.value })}
        />
      </section>
      <section className="card pad stack" style={{ gap: 10 }}>
        <div className="hstack" style={{ gap: 10 }}>
          <h2 className="h2 grow">{t("register.pedigreeTitle", { paz: r.paz })}</h2>
          {shown &&
            (shown.problems.length ? (
              <span className="chip r">{t(`register.problem.${shown.problems[0]}`)}</span>
            ) : (
              <span className="chip g">{t("register.validNotation")}</span>
            ))}
        </div>
        <TextInput
          className="mono"
          aria-label={t("register.pedigree")}
          value={pedigree}
          readOnly={!canEdit}
          onChange={(e) => setPedigree(e.currentTarget.value)}
        />
        {shown?.mother && shown.father && (
          <div className="hstack sm" style={{ gap: 10, flexWrap: "wrap" }}>
            <span className="mu">{t("register.interpretation")}</span>
            {[shown.mother, shown.father].map((p, i) => (
              <span key={p.text} className="hstack" style={{ gap: 6 }}>
                <span>{i ? "♂" : "♀"}</span> {p.text}
                {p.line ? (
                  <Link to="/genealogy/register" search={{ paz: p.line.key.paz, sid: p.line.key.sid }} className="chip b">
                    PAz {p.line.key.paz}
                  </Link>
                ) : (
                  <span className="chip y">{t("genotype.notInRegister")}</span>
                )}
              </span>
            ))}
          </div>
        )}
        {others.length > 0 && (
          <div className="hstack sm" style={{ gap: 6, flexWrap: "wrap" }}>
            <span className="mu">{t("register.otherCombinations")}</span>
            {others.map((l) => (
              <Link key={l.key.paz} to="/genealogy/register" search={{ paz: l.key.paz, sid: l.key.sid }} className="chip b">
                PAz {l.key.paz}
              </Link>
            ))}
          </div>
        )}
        {record.sisters > 1 && (
          <p className="note warn sm">
            {t("register.sisterWarning", { count: record.sisters })}{" "}
            <Link to="/genealogy/new-sid" search={{ tab: "sequence" }}>
              {t("register.sequenceReplace")}
            </Link>
          </p>
        )}
      </section>
      {canEdit && (
        <div className="hstack" style={{ gap: 8, justifyContent: "flex-end" }}>
          <Button variant="default" loading={addSid.isPending} onClick={() => setAskingSid(true)}>
            {t("register.newSid")}
          </Button>
          {askingSid && (
            <ConfirmModal
              title={t("register.newSid")}
              text={t("register.newSidConfirm", { paz: r.paz, pedigree: r.pedigre ?? "—" })}
              confirm={t("register.newSidButton")}
              loading={addSid.isPending}
              onConfirm={() => addSid.mutate({ paz: r.paz, data: {} }, { onSettled: () => setAskingSid(false) })}
              onClose={() => setAskingSid(false)}
            />
          )}
          <Button
            variant="default"
            disabled={!dirty}
            onClick={() => {
              setValues(init);
              setPedigree(r.pedigre ?? "");
            }}
          >
            {t("register.discard")}
          </Button>
          <Button
            disabled={!dirty || !!shown?.problems.length}
            loading={save.isPending}
            onClick={() => save.mutate({ paz: r.paz, sid: r.sid, data: { values: changed, pedigree, token: record.token } })}
          >
            {t("register.save")}
          </Button>
        </div>
      )}
    </>
  );
}

/** A supplementary tab of the line: its phenotype or passport row, or its Genes matrix cells. */
function SupplementTab({ paz, sid, tab, rights }: { paz: number; sid: number; tab: Exclude<Tab, "line">; rights: Rights }) {
  const data = useRegisterSupplements(paz, sid).data;
  if (!data) return <Loader size="sm" />;
  // keyed on the row's token: a save (or a change elsewhere) re-seeds the form from the server's values
  if (tab === "genes") return <GenesForm key={data.genes_token} paz={paz} sid={sid} data={data} canEdit={rights.genes} />;
  const token = tab === "pheno" ? data.pheno_token : data.kieg_token;
  return <RowForm key={`${tab}:${token}`} paz={paz} sid={sid} table={tab} data={data} canEdit={rights.supplements} />;
}

type Row = Record<string, string | number | null | undefined>;

/** The phenotype or passport row as a form; only the changed fields are sent, a field emptied is cleared. */
function RowForm({
  paz,
  sid,
  table,
  data,
  canEdit,
}: {
  paz: number;
  sid: number;
  table: "pheno" | "kieg";
  data: Supplements;
  canEdit: boolean;
}) {
  const { t } = useTranslation();
  const client = useQueryClient();
  const fields = table === "pheno" ? PHENO : KIEG;
  const init: Row = useMemo(() => ({ ...((table === "pheno" ? data.pheno : data.kieg) ?? {}) }), [data, table]);
  const [values, setValues] = useState<Row>(init);
  const changed = Object.fromEntries(
    fields.flatMap(([f]) => ((values[f] ?? null) !== (init[f] ?? null) ? [[f, values[f] === "" ? null : (values[f] ?? null)]] : [])),
  );
  const dirty = Object.keys(changed).length > 0;
  const onSuccess = () => {
    notifications.show({ color: "green", message: t(`register.supplementSaved.${table}`) });
    void client.invalidateQueries({ queryKey: getRegisterSupplementsQueryKey(paz, sid) });
  };
  const mutation = useRegisterSaveSupplement({ mutation: { onSuccess } });
  const token = table === "pheno" ? data.pheno_token : data.kieg_token;
  const save = () => mutation.mutate({ paz, sid, table, data: { values: changed as PhenoIO | KiegIO, token } });
  const set = (f: string, v: string | number | null) => setValues((x) => ({ ...x, [f]: v }));
  const missing = (table === "pheno" ? data.pheno : data.kieg) == null;
  return (
    <section className="card pad stack" style={{ gap: 12 }}>
      <div className="hstack" style={{ gap: 10 }}>
        <h2 className="h2 grow">{t(`register.tab.${table}`)}</h2>
        {missing && <span className="chip y">{t("register.noRowYet")}</span>}
      </div>
      <div className="tr-fields">
        {fields
          .filter(([, kind]) => kind !== "memo")
          .map(([f, kind]) => {
            const label = t(`register.${table}.${f}`);
            if (kind === "number")
              return (
                <NumberInput
                  key={f}
                  size="xs"
                  label={label}
                  min={0}
                  allowDecimal={false}
                  value={values[f] ?? ""}
                  readOnly={!canEdit}
                  onChange={(v) => set(f, num(v))}
                />
              );
            if (kind === "date")
              return (
                <DateField
                  key={f}
                  size="xs"
                  label={label}
                  value={String(values[f] ?? "")}
                  readOnly={!canEdit}
                  onChange={(v) => set(f, v || null)}
                />
              );
            return (
              <TextInput
                key={f}
                size="xs"
                label={label}
                value={String(values[f] ?? "")}
                readOnly={!canEdit}
                onChange={(e) => set(f, e.currentTarget.value)}
              />
            );
          })}
      </div>
      {fields
        .filter(([, kind]) => kind === "memo")
        .map(([f]) => (
          <Textarea
            key={f}
            size="xs"
            label={t(`register.${table}.${f}`)}
            autosize
            minRows={1}
            value={String(values[f] ?? "")}
            readOnly={!canEdit}
            onChange={(e) => set(f, e.currentTarget.value)}
          />
        ))}
      <span className="sm mu">{t(`register.supplementHelp.${table}`)}</span>
      {canEdit && (
        <div className="hstack" style={{ gap: 8, justifyContent: "flex-end" }}>
          <Button variant="default" disabled={!dirty} onClick={() => setValues(init)}>
            {t("register.discard")}
          </Button>
          <Button disabled={!dirty} loading={mutation.isPending} onClick={save}>
            {t("register.save")}
          </Button>
        </div>
      )}
    </section>
  );
}

/** The line's Genes matrix cells (a value per locus; the legacy Genes table's row): a value changed, cleared, or a
 *  locus added from the matrix's columns. */
function GenesForm({ paz, sid, data, canEdit }: { paz: number; sid: number; data: Supplements; canEdit: boolean }) {
  const { t } = useTranslation();
  const client = useQueryClient();
  const init = useMemo(() => Object.fromEntries(data.genes.map((c: GeneCell) => [c.locus, c.value])), [data]);
  const [values, setValues] = useState<Record<string, string>>(init);
  const shown = data.loci.filter((l) => l in values);
  const changed = Object.fromEntries(
    shown.flatMap((l) => {
      const v = (values[l] ?? "").trim();
      return v !== (init[l] ?? "") ? [[l, v || null]] : [];
    }),
  );
  const dirty = Object.keys(changed).length > 0;
  const save = useRegisterSaveGenes({
    mutation: {
      onSuccess: () => {
        notifications.show({ color: "green", message: t("register.genesSaved") });
        void client.invalidateQueries({ queryKey: getRegisterSupplementsQueryKey(paz, sid) });
      },
    },
  });
  return (
    <section className="card pad stack" style={{ gap: 12 }}>
      <div className="hstack" style={{ gap: 10 }}>
        <h2 className="h2 grow">{t("register.tab.genes")}</h2>
        <span className="sm mu">{t("register.genesCount", { count: data.genes.length, total: data.loci.length })}</span>
      </div>
      {shown.length ? (
        <div className="tr-fields">
          {shown.map((l) => (
            <TextInput
              key={l}
              size="xs"
              className="mono"
              label={l}
              value={values[l] ?? ""}
              readOnly={!canEdit}
              onChange={(e) => {
                const v = e.currentTarget.value;
                setValues((x) => ({ ...x, [l]: v }));
              }}
            />
          ))}
        </div>
      ) : (
        <p className="sm mu">{t("register.genesEmpty")}</p>
      )}
      <span className="sm mu">{t("register.genesHelp")}</span>
      {canEdit && (
        <div className="hstack" style={{ gap: 8 }}>
          <Select
            size="xs"
            w={220}
            searchable
            placeholder={hint("searchSelect")}
            aria-label={t("register.addLocus")}
            value={null}
            data={data.loci.filter((l) => !(l in values))}
            onChange={(l) => l && setValues((x) => ({ ...x, [l]: "" }))}
          />
          <span className="grow" />
          <Button variant="default" disabled={!dirty} onClick={() => setValues(init)}>
            {t("register.discard")}
          </Button>
          <Button
            disabled={!dirty}
            loading={save.isPending}
            onClick={() => save.mutate({ paz, sid, data: { cells: changed, token: data.genes_token } })}
          >
            {t("register.save")}
          </Button>
        </div>
      )}
    </section>
  );
}

/** Pedigree management → Save New, as the legacy: the PGM is required (a missing GH is only noted), and a pedigree the
 *  register has already offers a new SID under that combination instead (or a new combination anyway: a repeated
 *  cross). Without a pedigree a variety or line name is needed. */
function NewCombination({
  opened,
  onClose,
  onCreated,
}: {
  opened: boolean;
  onClose: () => void;
  onCreated: (paz: number, sid: number) => void;
}) {
  const { t } = useTranslation();
  const settings = useSettings();
  const invalidate = useInvalidateRegister();
  const [pedigree, setPedigree] = useState("");
  const [values, setValues] = useState<ValuesIO>({});
  const check = usePedigreeCheck(pedigree);
  const defaults: ValuesIO = { pgm: settings?.default_pgm, gh: settings?.default_gh };
  const entered: ValuesIO = { ...values, pgm: values.pgm || defaults.pgm, gh: values.gh || defaults.gh };
  const done = (message: string) => (out: { key?: { paz: number; sid: number } | null }) => {
    notifications.show({ color: "green", message: t(message, { key: out.key ? keyText(out.key) : "" }) });
    invalidate(null, true);
    setPedigree("");
    setValues({});
    onClose();
    if (out.key) onCreated(out.key.paz, out.key.sid);
  };
  const create = useRegisterCreate({ mutation: { onSuccess: done("register.created") } });
  const addSid = useRegisterAddSid({ mutation: { onSuccess: done("register.newSidDone") } });
  const named = pedigree.trim() || values.fajta || values.torzs_nev || values.rov;
  const existing = check?.existing ?? [];
  // a sister line takes the form's values that are filled (the legacy NewSID: PGM, GH and the names when given)
  const sister = Object.fromEntries(Object.entries(entered).filter(([, v]) => v)) as ValuesIO;
  return (
    <Modal opened={opened} onClose={onClose} title={t("register.newCombination")} size="lg">
      <div className="stack" style={{ gap: 10 }}>
        <TextInput
          className="mono"
          label={t("register.pedigree")}
          description={t("register.newPedigreeHelp")}
          value={pedigree}
          onChange={(e) => setPedigree(e.currentTarget.value)}
          data-autofocus
        />
        {check?.problems.length ? <span className="chip r">{t(`register.problem.${check.problems[0]}`)}</span> : null}
        {existing.length > 0 && (
          <div className="note warn stack" style={{ gap: 6 }}>
            <span className="sm">{t("register.existingPedigree", { count: existing.length })}</span>
            {existing.map((l) => (
              <div key={l.key.paz} className="hstack sm" style={{ gap: 8 }}>
                <span className="mono">PAz {l.key.paz}</span>
                <span className="grow">{l.name ?? "—"}</span>
                <Button
                  size="compact-xs"
                  variant="default"
                  loading={addSid.isPending}
                  onClick={() => addSid.mutate({ paz: l.key.paz, data: sister })}
                >
                  {t("register.newSidUnder", { paz: l.key.paz })}
                </Button>
              </div>
            ))}
          </div>
        )}
        <div className="tr-fields">
          {NEW_FIELDS.map((f) => (
            <TextInput
              key={f}
              size="xs"
              label={t(`register.field.${f}`)}
              placeholder={defaults[f] ?? undefined}
              value={values[f] ?? ""}
              onChange={(e) => setValues((v) => ({ ...v, [f]: e.currentTarget.value }))}
            />
          ))}
        </div>
        {!entered.pgm && <span className="chip r">{t("register.pgmRequired")}</span>}
        {entered.pgm && !entered.gh && <span className="sm mu">{t("register.ghMissing")}</span>}
        <div className="hstack" style={{ justifyContent: "flex-end", gap: 8 }}>
          <Button variant="default" onClick={onClose}>
            {t("register.cancel")}
          </Button>
          <Button
            variant={existing.length ? "default" : "filled"}
            disabled={!named || !entered.pgm || !!check?.problems.length}
            loading={create.isPending}
            onClick={() => create.mutate({ data: { pedigree: pedigree.trim() || null, values: entered, force: existing.length > 0 } })}
          >
            {existing.length ? t("register.newAnyway") : t("register.create")}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

/** A recently opened line (its sheet or tree) opened in the register instead: the key its link carries. */
function registerHref(item: WorkItem): string | null {
  const q = new URLSearchParams(item.href.split("?")[1] ?? "");
  const paz = q.get("paz");
  const sid = q.get("sid");
  return paz && sid ? `/genealogy/register?paz=${paz}&sid=${sid}` : null;
}
